import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Button,
  Center,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';

import { AddCircleOutline, SearchNormalOutline, Table } from '@aslanpay/portal-common-lib';

import CreateAdminModal from 'app/components/Admins/CreateAdmin/CreateAdminModal';
import DeleteIndividualModal from 'app/components/Individuals/DeleteIndividual/DeleteIndividualModal';
import InviteIndividualModal from 'app/components/Individuals/InviteIndividual/InviteIndividualModal';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';

import { defaultPageSize } from 'app/constants/table';

import useDebounce from 'app/hooks/useDebounce';
import useFetchAdminsPage from 'app/hooks/useFetchAdminsPage';

import { useManagingCompanyStore } from 'app/store';

import { getAdminProfilePath } from 'app/utils/routes';
import { getIndividualColumns } from 'app/utils/table/individuals';

function Admins() {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const {
    isOpen: isCreateAdminOpen,
    onOpen: onOpenCreateAdmin,
    onClose: onCloseCreateAdmin,
  } = useDisclosure();
  const {
    isOpen: isDeleteAdminOpen,
    onOpen: onOpenDeleteAdmin,
    onClose: onCloseDeleteAdmin,
  } = useDisclosure();
  const {
    isOpen: isInviteAdminOpen,
    onOpen: onOpenInviteAdmin,
    onClose: onCloseInviteAdmin,
  } = useDisclosure();

  const { company } = useManagingCompanyStore.getState();

  const [adminsActionIds, setAdminsActionIds] = useState<string[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('query') || '');

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const onSearchQueryChangedDebounced = useDebounce(handleSearchQueryChange);

  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data: admins, isLoading: isLoadingAdmins } = useFetchAdminsPage({
    companyId: [company?.id || ''],
    query: searchQuery,
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const handleOpenDeleteAdminModal = (ids: number[]) => {
    const adminIds = ids.map((id) => admins?.list[id]?.id || '');

    setAdminsActionIds(adminIds);
    onOpenDeleteAdmin();
  };

  const handleOpenInviteAdminModal = (ids: number[]) => {
    const adminIds = ids.map((id) => admins?.list[id]?.id || '');

    setAdminsActionIds(adminIds);
    onOpenInviteAdmin();
  };

  const handleEditProfile = (ids: number[]) => {
    const adminId = admins?.list[ids[0]]?.id || '';

    navigate(getAdminProfilePath(adminId));
  };

  const handleInvalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchAdminsPage'] });

    setAdminsActionIds([]);
    setRowSelection({});
  };

  const handleDeleteAdmins = () => {
    const totalPages = Math.ceil((admins?.total || 0) / pageSize);

    if (
      totalPages > 1 &&
      pageIndex === totalPages - 1 &&
      adminsActionIds.length === admins?.list.length
    ) {
      setPagination({ pageIndex: pageIndex - 1, pageSize: defaultPageSize });
    }

    handleInvalidateQueries();
  };

  const actions = {
    edit: handleEditProfile,
    invite: handleOpenInviteAdminModal,
    delete: handleOpenDeleteAdminModal,
  };

  const bulkActions = {
    invite: { action: handleOpenInviteAdminModal },
    delete: { action: handleOpenDeleteAdminModal },
  };

  useEffect(() => {
    setSearchParams(
      {
        page: `${pageIndex + 1}`,
        ...(searchQuery && { query: searchQuery }),
        ...(sorting[0] && { sortBy: sorting[0]?.id, sort: sorting[0]?.desc ? 'DESC' : 'ASC' }),
      },
      { replace: true }
    );
  }, [pageIndex, searchQuery, sorting, setSearchParams]);

  return (
    <AuthenticatedPageWrapper title="Admins">
      <HStack>
        <Text as="h2" textStyle="h2" color="brand.darkPurple">
          Admins
        </Text>
        <Spacer />
        <Button
          leftIcon={<AddCircleOutline boxSize="20px" />}
          size="sm"
          onClick={onOpenCreateAdmin}
        >
          Create new
        </Button>
        <CreateAdminModal isOpen={isCreateAdminOpen} onClose={onCloseCreateAdmin} />
        <InviteIndividualModal
          selectedIndividuals={adminsActionIds}
          isOpen={isInviteAdminOpen}
          onClose={onCloseInviteAdmin}
          onInvite={handleInvalidateQueries}
          type="admin"
        />
        <DeleteIndividualModal
          selectedIndividuals={adminsActionIds}
          isOpen={isDeleteAdminOpen}
          onClose={onCloseDeleteAdmin}
          onDelete={handleDeleteAdmins}
          type="admin"
        />
      </HStack>
      <Flex justify="space-between" gap="30px" alignItems="center">
        <Text my="10px" as="h5" textStyle="h5SemiBold" color="brand.darkGray">
          Admins list
        </Text>
        <Spacer />
        <InputGroup
          maxW="278px"
          aria-disabled={!searchQuery.length && !admins?.list.length}
          color="brand.darkGray"
          _disabled={{ color: 'system.disabled' }}
        >
          <Input
            isDisabled={!searchQuery.length && !admins?.list.length}
            onChange={(event) => onSearchQueryChangedDebounced(event.target.value)}
            defaultValue={searchQuery}
            type="text"
            placeholder="Search"
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>
      </Flex>
      {isLoadingAdmins ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <Table
          columns={getIndividualColumns('admin', actions)}
          data={admins?.list || []}
          total={admins?.total || 0}
          pageNumber={admins?.pageNumber || pageIndex}
          pageSize={admins?.pageSize || pageSize}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          getRowId={(admin) => admin.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={bulkActions}
          bulkActionsType="admin"
        />
      )}
    </AuthenticatedPageWrapper>
  );
}

export default Admins;
